import {
  alpha,
  useColorScheme,
  useTheme
} from "@mui/material";
import { useMemo, useState } from "react";
import { usePreviewTheme } from "../../context/StoredThemes.js";
function usePalette() {
  const { palette } = useTheme();
  const {
    api: { setPalette: setPreviewPalette },
    previewTheme: { colorSchemes }
  } = usePreviewTheme();
  const { colorScheme = "light" } = useColorScheme();
  const previewPalette = typeof colorSchemes?.[colorScheme] === "boolean" ? void 0 : colorSchemes?.[colorScheme]?.palette;
  const [expanded, setExpanded] = useState(false);
  return useMemo(() => {
    const handleOnExpand = (id, newExpanded) => {
      setExpanded(newExpanded ? id : false);
    };
    const handleColorPaletteChange = (id, key, color) => {
      const nextPalette = previewPalette ? { ...previewPalette } : {};
      const semanticColor = nextPalette[id] ? { ...nextPalette[id] } : { main: palette[id].main };
      semanticColor[key] = color;
      nextPalette[id] = semanticColor;
      setPreviewPalette(nextPalette, colorScheme);
    };
    const handleColorPaletteReset = (id, key) => {
      const nextPalette = previewPalette ? { ...previewPalette } : {};
      if (nextPalette[id]) {
        const semanticColor = {
          ...nextPalette[id]
        };
        delete semanticColor[key];
        if (key !== "main" && !semanticColor.main) {
          semanticColor.main = palette[id].main;
        }
        nextPalette[id] = semanticColor;
        setPreviewPalette(nextPalette, colorScheme);
      }
    };
    const handleOnResetColor = (id) => {
      if (previewPalette) {
        const nextPalette = { ...previewPalette };
        delete nextPalette[id];
        setPreviewPalette(nextPalette, colorScheme);
      }
    };
    const handleColorPropertyChange = (id, key, color) => {
      const nextPalette = previewPalette ? { ...previewPalette } : {};
      const sourceColor = palette[id];
      if (sourceColor && typeof sourceColor === "object") {
        nextPalette[id] = { ...nextPalette[id], [key]: color };
        setPreviewPalette(nextPalette, colorScheme);
      }
    };
    const handleColorPropertyReset = (id, key) => {
      if (previewPalette) {
        const nextPalette = { ...previewPalette };
        const sourceColor = nextPalette[id];
        if (sourceColor && typeof sourceColor === "object") {
          const targetColor = { ...sourceColor };
          delete targetColor[key];
          if (targetColor) {
            delete nextPalette[id];
          } else {
            nextPalette[id] = targetColor;
          }
          setPreviewPalette(nextPalette, colorScheme);
        }
      }
    };
    const createSetPaletteAsPrimary = (id) => {
      return () => {
        const nextPalette = previewPalette ? { ...previewPalette } : {};
        const primaryColor = palette.primary;
        if (!primaryColor) {
          console.error("No primary color found in palette");
          return;
        }
        const primaryKeys = new Set(Object.keys(primaryColor));
        const sourceColor = palette[id];
        if (sourceColor && typeof sourceColor === "object") {
          const sourceHasAllPrimaryKeys = Object.keys(sourceColor).every((key) => primaryKeys.has(key));
          if (!sourceHasAllPrimaryKeys) {
            console.error("Source color does not have all primary keys");
            return;
          }
          const sourceColorWithUpdates = { ...sourceColor, ...nextPalette[id] };
          nextPalette.primary = sourceColorWithUpdates;
          setPreviewPalette(nextPalette, colorScheme);
        }
      };
    };
    function createSetActionsOpacity(actionColorId) {
      return function setActionsOpacity(value) {
        const nextPalette = previewPalette ? { ...previewPalette } : {};
        if (!nextPalette.action) {
          nextPalette.action = {};
        }
        const opacityMap = {
          active: "activatedOpacity",
          disabled: "disabledOpacity",
          focus: "focusOpacity",
          hover: "hoverOpacity",
          selected: "selectedOpacity"
        };
        nextPalette.action[opacityMap[actionColorId]] = value;
        const baseColor = palette.action[actionColorId];
        nextPalette.action[actionColorId] = alpha(baseColor, value);
        setPreviewPalette(nextPalette, colorScheme);
      };
    }
    return {
      createSetActionsOpacity,
      createSetPaletteAsPrimary,
      expanded,
      handleColorPaletteChange,
      handleColorPaletteReset,
      handleColorPropertyChange,
      handleColorPropertyReset,
      handleOnExpand,
      handleOnResetColor,
      palette,
      previewPalette
    };
  }, [previewPalette, setPreviewPalette, expanded, palette, colorScheme]);
}
const actionOpacityMap = {
  active: true,
  disabled: true,
  focus: true,
  hover: true,
  selected: true
};
function isKeyOfActionOpacity(key) {
  return key in actionOpacityMap;
}
export {
  isKeyOfActionOpacity,
  usePalette
};
//# sourceMappingURL=usePalette.js.map
